import { PosSessionsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CloseSessionStatsDialogComponent } from '@posComponents';
import { CloseSessionStatsDialogData } from '@posModels';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as PosSessionsActions from './pos-sessions.actions';

@Injectable()
export class PosSessionsEffects {
  private actions$ = inject(Actions);
  private posApi = inject(PosSessionsApiService);
  private notification = inject(NotificationService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  loadPosSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadPosSessions),
      switchMap(({ params }) =>
        this.posApi.findAll(params).pipe(
          map((response) => PosSessionsActions.loadPosSessionsSuccess({ response })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_pos_sessions_error');
            return of(PosSessionsActions.loadPosSessionsFailure({
              error: error.message || 'Failed to load POS sessions',
            }));
          }),
        ),
      ),
    ),
  );

  loadCurrentSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadCurrentSession),
      switchMap(() =>
        this.posApi.getCurrentSession().pipe(
          map((session) => PosSessionsActions.loadCurrentSessionSuccess({ session })),
          catchError((error: any) => {
            return of(PosSessionsActions.loadCurrentSessionFailure({
              error: error.message || 'Failed to load current POS session',
            }));
          }),
        ),
      ),
    ),
  );

  openSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.openSession),
      switchMap(({ body }) =>
        this.posApi.open(body).pipe(
          map((session) => {
            this.notification.success('admin.notifications.pos_session_opened');
            return PosSessionsActions.openSessionSuccess({ session });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.open_pos_session_error');
            return of(PosSessionsActions.openSessionFailure({
              error: error.message || 'Failed to open POS session',
            }));
          }),
        ),
      ),
    ),
  );

  closeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.closeSession),
      switchMap(({ id, body }) =>
        this.posApi.close(id, body).pipe(
          map((response) => {
            this.notification.success('admin.notifications.pos_session_closed');
            return PosSessionsActions.closeSessionSuccess({ response });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.close_pos_session_error');
            return of(PosSessionsActions.closeSessionFailure({
              error: error.message || 'Failed to close POS session',
            }));
          }),
        ),
      ),
    ),
  );

  closeSessionShowStats$ = createEffect(() =>
      this.actions$.pipe(
        ofType(PosSessionsActions.closeSessionSuccess),
        tap(({ response }) => {
          const dialogData: CloseSessionStatsDialogData = { response };
          this.dialog.open(CloseSessionStatsDialogComponent, {
            data: dialogData,
            panelClass: 'custom-dialog-panel',
            backdropClass: 'custom-dialog-backdrop',
            width: '600px',
            disableClose: false,
          });
        }),
      ),
    { dispatch: false },
  );

  closeSessionNavigate$ = createEffect(() =>
      this.actions$.pipe(
        ofType(PosSessionsActions.closeSessionSuccess),
        tap(() => this.router.navigate([ '/pos/open-session' ])),
      ),
    { dispatch: false },
  );

  loadOverallStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadOverallStats),
      switchMap(({ params }) =>
        this.posApi.getOverallStats(params).pipe(
          map((stats) => PosSessionsActions.loadOverallStatsSuccess({ stats })),
          catchError((error: any) => {
            return of(PosSessionsActions.loadOverallStatsFailure({
              error: error.message || 'Failed to load overall stats',
            }));
          }),
        ),
      ),
    ),
  );

  loadSessionStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadSessionStats),
      switchMap(({ id }) =>
        this.posApi.getStats(id).pipe(
          map((stats) => PosSessionsActions.loadSessionStatsSuccess({ stats })),
          catchError((error: any) => {
            return of(PosSessionsActions.loadSessionStatsFailure({
              error: error.message || 'Failed to load session stats',
            }));
          }),
        ),
      ),
    ),
  );

  loadDetailedStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadDetailedStats),
      switchMap(({ id }) =>
        this.posApi.getDetailedStats(id).pipe(
          map((stats) => PosSessionsActions.loadDetailedStatsSuccess({ stats })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_detailed_stats_error');
            return of(PosSessionsActions.loadDetailedStatsFailure({
              error: error.message || 'Failed to load detailed stats',
            }));
          }),
        ),
      ),
    ),
  );

  loadHelpers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.loadHelpers),
      switchMap(({ sessionId }) =>
        this.posApi.getHelpers(sessionId).pipe(
          map((helpers) => PosSessionsActions.loadHelpersSuccess({ helpers })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_helpers_error');
            return of(PosSessionsActions.loadHelpersFailure({
              error: error.message || 'Failed to load helpers',
            }));
          }),
        ),
      ),
    ),
  );

  addHelper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.addHelper),
      switchMap(({ sessionId, body }) =>
        this.posApi.addHelper(sessionId, body).pipe(
          map((session) => {
            this.notification.success('admin.notifications.helper_added');
            return PosSessionsActions.addHelperSuccess({ session });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.add_helper_error');
            return of(PosSessionsActions.addHelperFailure({
              error: error.message || 'Failed to add helper',
            }));
          }),
        ),
      ),
    ),
  );

  reloadHelpersAfterAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.addHelperSuccess),
      map(({ session }) => PosSessionsActions.loadHelpers({ sessionId: session.id })),
    ),
  );

  removeHelper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.removeHelper),
      switchMap(({ sessionId, volunteerId }) =>
        this.posApi.removeHelper(sessionId, volunteerId).pipe(
          map((session) => {
            this.notification.success('admin.notifications.helper_removed');
            return PosSessionsActions.removeHelperSuccess({ session });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.remove_helper_error');
            return of(PosSessionsActions.removeHelperFailure({
              error: error.message || 'Failed to remove helper',
            }));
          }),
        ),
      ),
    ),
  );

  reloadHelpersAfterRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PosSessionsActions.removeHelperSuccess),
      map(({ session }) => PosSessionsActions.loadHelpers({ sessionId: session.id })),
    ),
  );
}

